import { cn, useTranslation } from '@hbs-us/ui';
import { Link, useRouterState } from '@tanstack/react-router';

import { useAccess } from '../../auth/useAccess.js';
import { useFeatureAccess } from '../../auth/useFeatureAccess.js';
import { LAUNCHER_GROUPS } from '../navData.js';
import { Flyout } from './Flyout.js';
import { SMALL_LABEL_CLASS } from './labels.js';

/**
 * The app launcher: a flyout grid of icon + label tiles under the five group
 * headers (Work · Money · People · Assets · Insight), holding every app the
 * tenant may reach — the ONE nav list files every destination, and the same
 * access + tenant-feature rules the sidebar applies filter the tiles here.
 * Settings is deliberately absent: it lives behind the profile/gear menu.
 *
 * A group whose tiles all filter away is dropped, so nobody gets a header over
 * nothing (the same rule the sidebar's useShellNav applies).
 */

/**
 * THE SIGNATURE. The launcher trigger is the most-pressed control in this chrome,
 * and off the shelf it wears the same nine dots as every other product's app grid.
 * QELM's own mark is not dots: it is a gauge — a needle sweeping a dial whose face
 * and side rule are drawn as graduation ticks. So the trigger is nine graduations,
 * not nine dots. The 3×3 field keeps the affordance every user already knows; the
 * marks inside it are the brand's, and no other product's launcher is built from
 * a measuring scale.
 *
 * Drawn on the lucide grid — 24×24, round caps, `currentColor` — so it sits IN the
 * same icon set as the bell and the gear rather than beside it.
 *
 * ⚠ THE THREE NUMBERS BELOW MOVE TOGETHER. At the first sizing the marks read as a
 * texture rather than as nine things: centres 6 apart, a 2.5-long tick and a stroke of
 * 2 put only 1.5 units of air between one mark and the next, while each mark was
 * 4.5 units long. Growing the marks alone closes that gap further and makes it worse.
 * Measured at the sizing below: the marks are 5.3 units long and 2.5 wide with 2.7
 * units of air, so the air is now wider than the mark instead of half of it.
 */
const MARK_CENTRES = [4, 12, 20];
const MARK_HALF_LENGTH = 1.4;
const MARK_STROKE = 2.5;

function LauncherMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={MARK_STROKE}
      strokeLinecap="round"
      className={className}
      aria-hidden="true"
    >
      {MARK_CENTRES.map((x) =>
        MARK_CENTRES.map((y) => (
          <line
            key={`${x}-${y}`}
            x1={x}
            y1={y - MARK_HALF_LENGTH}
            x2={x}
            y2={y + MARK_HALF_LENGTH}
          />
        )),
      )}
    </svg>
  );
}

// A tile is a nav row stood on end: the kit SidebarItem's radius, orange active
// pill and neutral hover, so the launcher reads as the rail it replaces.
//
// The proportion is decided, not defaulted. The box is 72px, the mark is 22px, and
// the mark is anchored 10px from the top rather than floated in the middle — so a
// one-word tile and a two-word tile put their icons on the SAME line across a row,
// and the mark reads as the tile's subject instead of a speck adrift in it.
//
// `min-h`, not `h`: 72px is the floor, not a lid. A fixed height plus `line-clamp-2`
// shaves a real app's name off the bottom the moment a person turns their browser
// font up, and the reader who most needs the label is the one who loses it. Grid
// items stretch, so every tile in a row still ends on the same line.
//
// A tile is ICON-led — the mark is 22px and the label 12px, so the mark is the
// thing being recognised. It therefore carries the same ink as its label instead of
// being greyed to a second rank behind the word that was only meant to confirm it.
//
// ⚠ A TILE IS BOUNDED AT REST. It used to be a bare icon over the panel surface, and the
// only thing that separated one app from the next was hover — which is not a resting
// state, so at rest there was no grid to read, just icons on a white sheet. The border
// draws the grid; hover still fills it, and the active app still wears the orange pill.
const TILE_CLASS =
  'flex min-h-[4.5rem] w-full flex-col items-center gap-1.5 rounded-md border px-1 pt-2.5 text-center ' +
  'font-medium transition-colors duration-[var(--dur-quick)] ease-[var(--ease-out)] motion-reduce:transition-none ' +
  '[&_svg]:size-[22px] [&_svg]:shrink-0 focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-popover';

// Two lines of `text-xs`/`leading-tight` — the slot every label gets whether it
// needs one line or two, so a long name can neither shove its own icon up nor be
// shaved off the bottom of the tile.
const TILE_LABEL_SLOT = 'flex min-h-[1.875rem] w-full items-start justify-center';

export function Launcher() {
  const { t } = useTranslation();
  const canAccess = useAccess();
  const canSeeFeature = useFeatureAccess();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const groups = LAUNCHER_GROUPS.map((group) => ({
    labelKey: group.labelKey,
    items: group.items
      .filter((item) => canSeeFeature(item.to))
      .filter((item) => canAccess(item.access)),
  })).filter((group) => group.items.length > 0);

  return (
    <Flyout
      label={t('nav:launcher.openApps')}
      trigger={<LauncherMark className="size-6" />}
      /*
       * ⛔ A RIGHT-ANCHORED PANEL WIDER THAN ITS TRIGGER'S OFFSET RUNS OFF THE LEFT EDGE.
       * The launcher used to sit at x=16 on a phone and hang its 360px panel to the
       * right, which fitted. From the right of the bar its own right edge is at 328 on a
       * 390px screen, so the same panel would start at -38 and the page would scroll
       * sideways. Below `sm` it therefore leaves the bar entirely and becomes the same
       * bottom sheet the New menu uses — which is also where a thumb already is, and
       * makes the two panels in this bar behave alike.
       */
      panelClassName={cn(
        'right-0 origin-top-right w-[22.5rem] max-w-[calc(100vw-1.5rem)] px-3 pt-3 pb-1.5',
        'max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:mt-0 max-sm:w-auto',
        'max-sm:max-h-[75dvh] max-sm:max-w-none max-sm:rounded-b-none max-sm:rounded-t-2xl',
        'max-sm:pb-[calc(0.5rem+env(safe-area-inset-bottom))] max-sm:origin-bottom',
      )}
      sheetScrim
    >
      {/*
        A section label must sit closer to the tiles it names than to the group
        above it, or the eye cannot tell which side of it the label belongs to.
        Measured before: 5.7px from a header to its own tiles, 15.2px between
        groups — and once the tile's own 10px top inset and its empty label slot
        are counted, 15px versus 28px optically. Two near-equal gaps, so five
        groups read as one continuous grey column. The between-group gap now runs
        at more than twice the header's own gap in both the raw and the optical
        measure, which is what makes five blocks out of one list.
      */}
      <nav aria-label={t('nav:launcher.openApps')} className="grid gap-6">
        {groups.map((group) => (
          <section key={group.labelKey} className="grid gap-1.5">
            <p className={cn(SMALL_LABEL_CLASS, 'px-1')}>{t(group.labelKey)}</p>
            <ul className="grid grid-cols-3 gap-1.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = pathname === item.to || pathname.startsWith(`${item.to}/`);
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        TILE_CLASS,
                        active
                          ? // The pill is the shape; a rule drawn around a filled tile would
                            // read as a second edge inside the one the colour already makes.
                            'border-transparent bg-sidebar-accent text-sidebar-accent-foreground'
                          : 'border-border text-foreground hover:bg-secondary',
                      )}
                    >
                      <Icon aria-hidden="true" />
                      <span className={TILE_LABEL_SLOT}>
                        <span className="line-clamp-2 text-xs leading-tight">
                          {t(item.labelKey)}
                        </span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </nav>
    </Flyout>
  );
}
